import Icon from '../ui/Icon';
import FuelPill from './FuelPill';
import StatusPill from './StatusPill';
import RowActionsMenu from './RowActionsMenu';
import { PLACEHOLDER } from './userAdapter';
import { copyText } from '../../utils/clipboard';

// Sticky cells need their own background so scrolled columns don't show through
export const STICKY_CELL = 'sticky z-10 bg-surface-container-lowest group-hover:bg-surface-container-low';
export const NUMBER_COL = 'left-0 w-14 min-w-14';
export const USER_COL = 'left-14 min-w-[240px] shadow-[1px_0_0_0_var(--color-surface-container)]';

/** One table row. `user` is a `UserRowView` from the adapter; text is always rendered as plain text. */
export default function UserRow({ user, actions }) {
  return (
    <tr className="group h-14 border-b border-surface-container last:border-b-0 hover:bg-surface-container-low transition-colors">
      <td className={`${STICKY_CELL} ${NUMBER_COL} px-4 py-2.5 text-center font-data-mono text-xs text-on-surface-variant font-medium`}>
        {user.position}
      </td>

      <th scope="row" className={`${STICKY_CELL} ${USER_COL} px-4 py-2.5 text-left font-normal`}>
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-full text-[13px] font-semibold flex items-center justify-center shrink-0 shadow-xs ${user.avatarClass}`}
            aria-hidden="true"
          >
            {user.initials}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-base font-semibold text-on-surface truncate leading-tight">{user.name}</span>
            <span className="font-data-mono text-[11px] text-on-surface-variant truncate">{user.subtitle}</span>
          </div>
        </div>
      </th>

      <td className="px-4 py-2.5">
        {user.email ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] text-on-surface">{user.email}</span>
            {user.emailVerified && (
              <Icon name="verified" size={15} className="text-secondary" title="Verified email" />
            )}
            <button
              type="button"
              onClick={() => copyText(user.email)}
              aria-label={`Copy email address of ${user.name}`}
              title="Copy email"
              className="text-on-surface-variant hover:text-primary transition-colors p-0.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              <Icon name="content_copy" size={14} />
            </button>
          </div>
        ) : (
          <span className="text-on-surface-variant">{PLACEHOLDER}</span>
        )}
      </td>

      <td className="px-4 py-2.5 font-data-mono text-xs text-on-surface whitespace-nowrap">{user.phone}</td>

      <td className="px-4 py-2.5">
        <FuelPill fuel={user.fuel} />
      </td>

      <td className="px-4 py-2.5">
        <div className="flex items-center justify-between gap-3 w-36 text-[11px]">
          <span className="font-bold text-on-surface font-data-mono whitespace-nowrap">{user.scansLabel}</span>
          {user.scansToNextTier != null && (
            <span className="text-[10px] text-on-surface-variant whitespace-nowrap">
              {user.scansToNextTier === 0 ? 'Tier Max' : `${user.scansToNextTier} to next tier`}
            </span>
          )}
        </div>
      </td>

      <td className="px-4 py-2.5">
        {user.couponsEarned == null && !user.tier ? (
          <span className="text-on-surface-variant" title="Not available yet">
            {PLACEHOLDER}
          </span>
        ) : (
          <div className="flex items-center gap-2">
            {user.couponsEarned != null && (
              <span className="text-[13px] font-semibold text-on-surface whitespace-nowrap">
                {user.couponsEarned} {user.couponsEarned === 1 ? 'coupon' : 'coupons'}
              </span>
            )}
            {user.tier && (
              <span className="px-2 py-0.5 rounded-full bg-surface-container text-on-surface text-[11px] font-semibold whitespace-nowrap">
                {user.tier}
              </span>
            )}
          </div>
        )}
      </td>

      <td className="px-4 py-2.5 pr-6 text-right">
        <div className="inline-flex items-center gap-2">
          <StatusPill status={user.status} />
          <RowActionsMenu label={`Actions for ${user.name}`} items={actions} />
        </div>
      </td>
    </tr>
  );
}
